import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from './toastContext';
import s from './Toast.module.css';
import { t } from '@app/i18n';

/** Tempo de exibição do protótipo (`.toast` em `docs/design/zarya-components.css`). */
export const TOAST_DURATION_MS = 2800;

const ICONE: Record<ToastTone, IconName> = {
  sucesso: 'check-circle',
  erro: 'shield-alert',
  aviso: 'warning',
  info: 'info',
};

export interface ToastProps {
  open: boolean;
  texto: ReactNode;
  tone?: ToastTone;
  /** `0` desliga o fechamento automático. */
  duracaoMs?: number;
  onClose?: () => void;
}

/**
 * Aviso efêmero no rodapé (`.toast` do protótipo).
 *
 * `role="status"` (região viva educada — não interrompe o leitor de tela) e
 * `data-testid="toast"`: os dois seletores que o `BasePage` do E2E procura.
 */
export function Toast({
  open,
  texto,
  tone = 'sucesso',
  duracaoMs = TOAST_DURATION_MS,
  onClose,
}: ToastProps) {
  useEffect(() => {
    if (!open || !onClose || duracaoMs <= 0) return;
    const id = window.setTimeout(onClose, duracaoMs);
    return () => window.clearTimeout(id);
  }, [open, onClose, duracaoMs]);

  if (!open) return null;

  return (
    <div
      className={[s.toast, s[tone]].filter(Boolean).join(' ')}
      role="status"
      aria-live="polite"
      data-testid="toast"
      data-tone={tone}
    >
      <Icon name={ICONE[tone]} className={s.ic} aria-hidden="true" />
      <span className={s.texto}>{texto}</span>
      {onClose ? (
        <button
          type="button"
          className={s.fechar}
          onClick={onClose}
          aria-label={t('common.ui.closeNotice')}
        >
          <Icon name="close" size="xs" />
        </button>
      ) : null}
    </div>
  );
}

interface ToastState {
  texto: string;
  tone: ToastTone;
  duracaoMs: number;
  /** Reinicia o cronômetro quando um aviso substitui o outro (vira `key`). */
  seq: number;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Duração padrão dos avisos desta árvore. */
  duracaoMs?: number;
}

/** Fila (de um) do toast: `useToast().mostrar('…')` de qualquer tela. */
export function ToastProvider({ children, duracaoMs = TOAST_DURATION_MS }: ToastProviderProps) {
  const [current, setAtual] = useState<ToastState | null>(null);

  const close = useCallback(() => setAtual(null), []);

  const mostrar = useCallback(
    (texto: string, options?: ToastOptions) => {
      setAtual((previous) => ({
        texto,
        tone: options?.tone ?? 'sucesso',
        duracaoMs: options?.duracaoMs ?? duracaoMs,
        seq: (previous?.seq ?? 0) + 1,
      }));
    },
    [duracaoMs],
  );

  const api = useMemo<ToastApi>(() => ({ mostrar, fechar: close }), [mostrar, close]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {current ? (
        <Toast
          key={current.seq}
          open
          texto={current.texto}
          tone={current.tone}
          duracaoMs={current.duracaoMs}
          onClose={close}
        />
      ) : null}
    </ToastContext.Provider>
  );
}
